import CopilotWidget from '../components/CopilotWidget'
/**
 * CoConnect — Factory Clerk Dashboard (v1.5.2 REBUILD)
 * ───────────────────────────────────────────────────────────
 * Previously this file was dead code — not mounted in the router and calling
 * a non-existent /batches/:id/intake endpoint. v1.5.2 rebuilds it with:
 *   1. Proper design system (Card, SegmentedTabs, Sheet, etc.)
 *   2. Correct endpoint: PUT /api/batches/:id/intake (added in v1.5.2)
 *   3. AI-powered quality grading (calls POST /api/ai/quality/grade)
 *   4. Full intake form with all quality metrics (moisture, defects, bean count)
 *   5. Accessibility: <label> on every field, <h1> page title
 *   6. Three tabs: Pending Intake, Inventory, Daily Summary
 *
 * v1.7.1 CLERK PATCH (additive — original logic preserved 100%):
 *   C-01: Intake queue now also includes DISPATCHED + IN_TRANSIT batches (state machine compliant)
 *   C-02: Real-time variance detection at intake time (expected vs actual weight)
 *   M-01: Confirmation dialog before submitting intake
 *   M-02: Driver/truck/dispatch info shown on pending batches table
 *   M-03: Search/filter input on pending batches (by farmer name or batch id)
 *   M-04: Design-system Sheet replaces native prompt() for variance investigation
 *   M-05: File upload with preview for bean photo (base64 still accepted as fallback)
 *   M-06: WCAG 2.2 AA skip-to-content link
 *   L-01: Parallel fetch of /chain/stats for pending decisions counter
 *   L-02: Loading skeleton when switching tabs
 *   L-03: Date picker on Daily Summary for historical review
 *   L-04: "Inventory" tab renamed to "Agro Products" (clarity — warehouse stock lives in WarehouseDashboard)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LogoutButton from '../components/LogoutButton';
import ProfileButton from '../components/ProfileButton';
import {
  BrandMark, Card, SectionHeader, MetricTile, SmartStatus, StatusPill,
  Spinner, EmptyState, Alert, IconButton, SegmentedTabs, Sheet, Pagination, usePagination,
  Skeleton,
} from '../components/ui';

const Icon = {
  Package: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /><path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" /></svg>),
  Truck: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>),
  Check: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3" /></svg>),
  Sparkles: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" /></svg>),
  Refresh: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>),
  Search: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>),
  Upload: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" /></svg>),
  Alert: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" /></svg>),
  Calendar: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>),
};

type Tab = 'intake' | 'inventory' | 'summary' | 'sensory' | 'lab';

const FactoryClerkDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('intake');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [pendingBatches, setPendingBatches] = useState<any[]>([]);
  const [intakedBatches, setIntakedBatches] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [intakeSheet, setIntakeSheet] = useState<any | null>(null);
  const [aiGrade, setAiGrade] = useState<any | null>(null);
  const [aiGrading, setAiGrading] = useState(false);

  // v1.7.1 (L-01): chain stats — parallel non-blocking fetch for pending decisions counter
  const [chainStats, setChainStats] = useState<any | null>(null);

  // v1.7.1 (M-03): search filter on pending batches (by farmer name or batch id)
  const [pendingSearch, setPendingSearch] = useState('');

  // v1.7.1 (L-03): date picker for Daily Summary historical review
  const [summaryDate, setSummaryDate] = useState<string>(new Date().toISOString().slice(0, 10));

  // v1.7.1 (M-04): variance investigation Sheet (replaces native prompt())
  const [varianceSheet, setVarianceSheet] = useState<{ batchId: string; batch: any } | null>(null);
  const [varianceNotes, setVarianceNotes] = useState('');

  // v1.7.1 (L-02): per-tab loading skeleton (separate from initial load)
  const [tabLoading, setTabLoading] = useState(false);

  const pendingPag = usePagination(pendingBatches, 10);
  const intakedPag = usePagination(intakedBatches, 15);

  // v1.7.1 (M-03): filtered pending batches — original pendingBatches state is preserved unchanged
  const filteredPendingBatches = useMemo(() => {
    if (!pendingSearch.trim()) return pendingBatches;
    const q = pendingSearch.trim().toLowerCase();
    return pendingBatches.filter((b: any) =>
      (b.farmer_name || '').toLowerCase().includes(q) ||
      (b.id || '').toLowerCase().includes(q) ||
      (b.farmer_village || '').toLowerCase().includes(q)
    );
  }, [pendingBatches, pendingSearch]);
  const filteredPendingPag = usePagination(filteredPendingBatches, 10);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const safeGet = async (url: string) => {
        try { return await apiClient.get(url); } catch { return { data: [] }; }
      };
      // Physical receiving queue contains only confirmed delivered batches.
      const [deliveredRes, intakedRes, prodRes] = await Promise.all([
        safeGet('/batches?status=DELIVERED&limit=100'),
        safeGet('/batches?status=INTAKED&limit=100'),
        safeGet('/agro-products'),
      ]);

      const toArray = (r: any) =>
        Array.isArray(r?.data) ? r.data : (r?.data?.batches || []);

      const deliveredBatches = toArray(deliveredRes)
        .sort((a: any, b: any) => {
          const aTime = a.delivered_at
            ? new Date(a.delivered_at).getTime()
            : 0;
          const bTime = b.delivered_at
            ? new Date(b.delivered_at).getTime()
            : 0;
          return aTime - bTime;
        });

      const intakedBatchesArray = toArray(intakedRes);
      setPendingBatches(deliveredBatches);
      setIntakedBatches(intakedBatchesArray);
      setProducts(prodRes.data?.products || []);

      // v1.7.1 (L-01): warm chain stats in the background (non-blocking — failures are swallowed)
      safeGet('/chain/stats').then((r: any) => {
        if (r?.data?.stats) setChainStats(r.data.stats);
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── AI Grade Preview ─────────────────────────────────────
  const previewAiGrade = async (formData: any) => {
    setAiGrading(true);
    setAiGrade(null);
    try {
      const res = await apiClient.post('/ai/quality/grade', {
        moisture_content: formData.moisture_content ? Number(formData.moisture_content) : undefined,
        bean_count_per_100g: formData.bean_count_per_100g ? Number(formData.bean_count_per_100g) : undefined,
        fermentation_level: formData.fermentation_level,
        defect_insect_kg: formData.defect_insect_kg ? Number(formData.defect_insect_kg) : undefined,
        defect_mold_kg: formData.defect_mold_kg ? Number(formData.defect_mold_kg) : undefined,
        defect_flat_kg: formData.defect_flat_kg ? Number(formData.defect_flat_kg) : undefined,
        photo_base64: formData.batch_photo_url || undefined,
      });
      setAiGrade(res.data);
    } catch (err: any) {
      setError('AI grading failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setAiGrading(false);
    }
  };

  // ─── Submit Intake ────────────────────────────────────────
  const submitIntake = async (formData: any) => {
    setError(null); setSuccess(null);
    try {
      const res = await apiClient.put(`/batches/${intakeSheet.id}/intake`, {
        actual_weight: Number(formData.actual_weight),
        moisture_content: formData.moisture_content ? Number(formData.moisture_content) : undefined,
        fermentation_level: formData.fermentation_level,
        spoilage_kg: formData.spoilage_kg ? Number(formData.spoilage_kg) : undefined,
        bean_count_per_100g: formData.bean_count_per_100g ? Number(formData.bean_count_per_100g) : undefined,
        defect_insect_kg: formData.defect_insect_kg ? Number(formData.defect_insect_kg) : undefined,
        defect_mold_kg: formData.defect_mold_kg ? Number(formData.defect_mold_kg) : undefined,
        defect_flat_kg: formData.defect_flat_kg ? Number(formData.defect_flat_kg) : undefined,
        temperature_c: formData.temperature_c ? Number(formData.temperature_c) : undefined,
        humidity_pct: formData.humidity_pct ? Number(formData.humidity_pct) : undefined,
        batch_photo_url: formData.batch_photo_url || undefined,
        warehouse_section: formData.warehouse_section || undefined,
        notes: formData.notes || undefined,
        use_ai_grading: true,
      });
      setSuccess(`Batch intaked. Grade: ${res.data.grading.grade} (score: ${res.data.grading.score}). Amount: ${Number(res.data.batch.calculated_amount).toLocaleString()} XAF`);
      setIntakeSheet(null);
      setAiGrade(null);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Intake failed');
    }
  };

  // v1.6.0: Make a batch decision (EXPORT/FACTORY/REJECT/RECONDITION/DOWNGRADE)
  const handleBatchDecision = async (batchId: string, decision: string, reason?: string) => {
    setError(null); setSuccess(null);
    try {
      await apiClient.post(`/chain/batches/${batchId}/decision`, { decision, reason });
      setSuccess(`Batch routed to: ${decision}`);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Decision failed');
    }
  };

  // v1.6.0: Mark variance as investigated
  const handleInvestigateVariance = async (batchId: string, notes: string) => {
    setError(null); setSuccess(null);
    try {
      await apiClient.patch(`/chain/batches/${batchId}/variance`, { investigation_notes: notes });
      setSuccess('Variance investigation recorded');
      setVarianceSheet(null);
      setVarianceNotes('');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Investigation failed');
    }
  };

  // v1.7.1 (M-04): open the variance investigation Sheet (replaces native prompt())
  const openVarianceSheet = (batch: any) => {
    setVarianceSheet({ batchId: batch.id, batch });
    setVarianceNotes('');
  };

  // v1.7.1 (L-02): when user switches tabs, briefly show a skeleton (gives visual feedback)
  const handleTabChange = (t: any) => {
    if (t === activeTab) return;
    setTabLoading(true);
    setActiveTab(t as Tab);
    // Skeleton shows for 200ms — short enough to feel responsive, long enough to register
    setTimeout(() => setTabLoading(false), 200);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <BrandMark size="lg" accent="cargo" />
          <div className="flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400">
            <Spinner size="sm" /> Loading factory workspace…
          </div>
        </div>
      </div>
    );
  }

  // Daily summary calculations — v1.7.1 (L-03): now driven by summaryDate (defaults to today)
  const selectedDateObj = new Date(summaryDate + 'T00:00:00');
  const dayIntaked = intakedBatches.filter(b =>
    b.intaked_at && new Date(b.intaked_at).toDateString() === selectedDateObj.toDateString()
  );
  // Backward-compat alias (older code referenced todayIntaked) — preserves behavior for "today"
  const todayIntaked = intakedBatches.filter(b => b.intaked_at && new Date(b.intaked_at).toDateString() === new Date().toDateString());
  const todayWeight = dayIntaked.reduce((sum, b) => sum + Number(b.actual_weight || 0), 0);
  const todayAmount = dayIntaked.reduce((sum, b) => sum + Number(b.calculated_amount || 0), 0);
  const todayGradeA = dayIntaked.filter(b => b.quality_grade === 'A').length;

  return (
    <div className="factory-clerk-enterprise min-h-screen bg-ink-50 dark:bg-ink-950 pb-24 md:pb-0">
      {/* v1.7.1 (M-06): WCAG 2.2 AA skip-to-content link */}
      <a href="#clerk-main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-ink-900 focus:text-white focus:rounded-lg">
        Skip to content
      </a>
      <header className="topbar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <BrandMark size="md" accent="cargo" />
            <div className="min-w-0">
              <h1 className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">Factory Clerk workspace</h1>
              <p className="text-xs text-ink-500 dark:text-ink-400 truncate">
                {user?.name} · {user?.role}
                {/* v1.7.1 (L-01): show pending decisions counter from chain stats */}
                {chainStats && Number(chainStats.pending_decisions) > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 rounded-full bg-warning-100 dark:bg-warning-500/20 text-warning-700 dark:text-warning-400 text-2xs font-semibold">
                    {chainStats.pending_decisions} pending decisions
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {/* v1.6.6: link to warehouse portal */}
            <a href="/warehouse" className="btn-secondary text-xs hide-mobile">
              <Icon.Package className="w-3.5 h-3.5" />
              <span>Warehouse</span>
            </a>
            <button onClick={fetchData} disabled={refreshing} className="btn-secondary text-xs hide-mobile">
              <Icon.Refresh className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Syncing…' : 'Refresh'}</span>
            </button>
            <IconButton label="Refresh" onClick={fetchData} disabled={refreshing} className="md:hidden">
              <Icon.Refresh className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </IconButton>
            <ProfileButton />
            <LogoutButton />
          </div>
        </div>
      </header>

      <main id="clerk-main-content" className="factory-clerk-main max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-5">
        {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

        <section className="clerk-ops-strip" aria-label="Factory clerk operational queues">
          <button type="button" onClick={() => handleTabChange('intake')} className="clerk-ops-card">
            <span className="clerk-ops-icon"><Icon.Truck className="w-4 h-4" /></span>
            <span><small>Awaiting intake</small><strong>{pendingBatches.length}</strong><em>FIFO receiving queue</em></span>
          </button>
          <button type="button" onClick={() => handleTabChange('summary')} className="clerk-ops-card">
            <span className="clerk-ops-icon"><Icon.Check className="w-4 h-4" /></span>
            <span><small>Intaked today</small><strong>{todayIntaked.length}</strong><em>{todayIntaked.length === 1 ? 'batch processed' : 'batches processed'}</em></span>
          </button>
          <button type="button" onClick={() => handleTabChange('summary')} className="clerk-ops-card">
            <span className="clerk-ops-icon"><Icon.Alert className="w-4 h-4" /></span>
            <span><small>Pending decisions</small><strong>{Number(chainStats?.pending_decisions || 0)}</strong><em>Routing review</em></span>
          </button>
        </section>

        <SegmentedTabs
          items={[
            { id: 'intake', label: `Pending Intake (${pendingBatches.length})`, icon: <Icon.Truck className="w-4 h-4" /> },
            // v1.7.1 (L-04): renamed "Inventory" → "Agro Products" for clarity
            // (warehouse stock levels live in WarehouseDashboard, not here)
            { id: 'inventory', label: 'Agro Products', icon: <Icon.Package className="w-4 h-4" /> },
            { id: 'summary', label: 'Daily Summary', icon: <Icon.Check className="w-4 h-4" /> },
            // v1.9.2: Sensory QC + Lab test tabs for end-to-end quality flow
            { id: 'sensory', label: 'Sensory QC', icon: <Icon.Sparkles className="w-4 h-4" /> },
            { id: 'lab', label: 'Lab Tests', icon: <Icon.Package className="w-4 h-4" /> },
          ]}
          active={activeTab}
          onChange={handleTabChange}
        />

        {/* v1.7.1 (L-02): loading skeleton shown briefly when switching tabs */}
        {tabLoading ? (
          <Card padding="none" className="overflow-hidden">
            <div className="p-5 space-y-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-40" />
            </div>
            <div className="p-5 space-y-2">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          </Card>
        ) : (
        <>

        {/* ─── Pending Intake Tab ─── */}
        {activeTab === 'intake' && (
          <Card padding="none" className="overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800">
              <SectionHeader eyebrow="Batches awaiting intake" title="Pending Intake" subtitle="Batches that have arrived at the warehouse and need quality grading (PENDING + DISPATCHED + IN_TRANSIT)" />
              {/* v1.7.1 (M-03): search/filter input */}
              {pendingBatches.length > 0 && (
                <div className="mt-3 relative">
                  <Icon.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                  <input
                    type="search"
                    value={pendingSearch}
                    onChange={(e) => { setPendingSearch(e.target.value); filteredPendingPag.setPage(1); }}
                    placeholder="Search by farmer name, village, or batch ID…"
                    className="input pl-9 text-sm"
                    aria-label="Search pending batches"
                  />
                </div>
              )}
            </div>
            {pendingBatches.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Check className="w-6 h-6" />} title="No pending batches" description="All arrived batches have been intaked." /></div>
            ) : filteredPendingBatches.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Search className="w-6 h-6" />} title="No matches" description={`No batches match "${pendingSearch}"`} /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  {/* v1.7.1 (M-02): added Dispatch/Truck/Driver columns */}
                  <thead><tr><th>Batch ID</th><th>Farmer</th><th>Est. Weight</th><th>Collected</th><th>Status</th><th>Truck / Driver</th><th>Action</th></tr></thead>
                  <tbody>
                    {filteredPendingPag.paginatedItems.map((b: any) => (
                      <tr key={b.id}>
                        <td className="text-xs font-mono">{b.id.slice(0, 8)}…</td>
                        <td className="text-sm">{b.farmer_name || b.farmer_id?.slice(0, 8) || '—'}</td>
                        <td className="nums">{b.est_weight} kg</td>
                        <td className="text-2xs">{b.collected_at ? new Date(b.collected_at).toLocaleDateString() : '—'}</td>
                        <td><SmartStatus status={b.status} /></td>
                        {/* v1.7.1 (M-02): show dispatch info if available (from batches GET JOIN) */}
                        <td className="text-2xs">
                          {b.truck_reg || b.driver_name ? (
                            <div className="flex flex-col">
                              {b.truck_reg && <span className="font-mono">{b.truck_reg}</span>}
                              {b.driver_name && <span className="text-ink-500">{b.driver_name}</span>}
                            </div>
                          ) : (
                            <span className="text-ink-400">—</span>
                          )}
                        </td>
                        <td>
                          <button onClick={() => setIntakeSheet(b)} className="btn-primary text-xs">
                            <Icon.Package className="w-3 h-3" /> Intake
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="border-t border-ink-200 dark:border-ink-800">
              {/* v1.7.1 (M-03): paginating the filtered list (falls back to original pag when no search) */}
              <Pagination
                page={pendingSearch.trim() ? filteredPendingPag.page : pendingPag.page}
                pageSize={pendingSearch.trim() ? filteredPendingPag.pageSize : pendingPag.pageSize}
                total={pendingSearch.trim() ? filteredPendingPag.total : pendingPag.total}
                onPageChange={pendingSearch.trim() ? filteredPendingPag.setPage : pendingPag.setPage}
                onPageSizeChange={pendingSearch.trim() ? filteredPendingPag.setPageSize : pendingPag.setPageSize}
                showPageSize
              />
            </div>
          </Card>
        )}

        {/* ─── Inventory Tab (v1.7.1 L-04: renamed to Agro Products in nav) ─── */}
        {activeTab === 'inventory' && (
          <Card padding="none" className="overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800">
              <SectionHeader eyebrow="Agro-chemical registry" title="Agro Products" subtitle="MINADER-verified products catalog (read-only). Warehouse stock levels are in the Warehouse portal." />
            </div>
            {products.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Package className="w-6 h-6" />} title="No products" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead><tr><th>Trade Name</th><th>Type</th><th>Active Ingredients</th><th>Dosage</th><th>Price (XAF)</th></tr></thead>
                  <tbody>
                    {products.map((p: any) => (
                      <tr key={p.id}>
                        <td className="text-sm font-medium">{p.trade_name}</td>
                        <td><StatusPill tone={p.product_type === 'fungicide' ? 'info' : 'warning'} dot>{p.product_type}</StatusPill></td>
                        <td className="text-2xs">{p.active_ingredients}</td>
                        <td className="text-2xs">{p.dosage_per_15l || p.dosage_per_ha || '—'}</td>
                        <td className="nums">{p.average_price_xaf ? Number(p.average_price_xaf).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* ─── Daily Summary Tab ─── */}
        {activeTab === 'summary' && (
          <div className="space-y-4">
            {/* v1.7.1 (L-03): date picker for historical review */}
            <Card padding="none" className="overflow-hidden">
              <div className="p-4 flex flex-wrap items-center gap-3 border-b border-ink-200 dark:border-ink-800">
                <div className="flex items-center gap-2">
                  <Icon.Calendar className="w-4 h-4 text-ink-500" />
                  <label htmlFor="summary-date" className="text-sm font-medium text-ink-700 dark:text-ink-300">Date:</label>
                  <input
                    id="summary-date"
                    type="date"
                    value={summaryDate}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setSummaryDate(e.target.value)}
                    className="input text-sm w-auto"
                  />
                </div>
                <button
                  onClick={() => setSummaryDate(new Date().toISOString().slice(0, 10))}
                  className="btn-secondary text-xs"
                  disabled={summaryDate === new Date().toISOString().slice(0, 10)}
                >
                  Jump to today
                </button>
                <span className="text-2xs text-ink-500 ml-auto">
                  Showing: {selectedDateObj.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                </span>
              </div>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* v1.7.1 (L-03): labels now reflect selected date, not hardcoded "Today" */}
              <MetricTile label={`Batches Intaked (${summaryDate === new Date().toISOString().slice(0,10) ? 'Today' : summaryDate})`} value={dayIntaked.length} icon={<Icon.Package className="w-4 h-4" />} accent="cargo" />
              <MetricTile label="Total Weight" value={`${todayWeight.toFixed(1)} kg`} />
              <MetricTile label="Total Value" value={`${todayAmount.toLocaleString()} XAF`} />
              <MetricTile label="Grade A Batches" value={todayGradeA} accent="cargo" />
            </div>

            <Card padding="none" className="overflow-hidden">
              <div className="p-5 border-b border-ink-200 dark:border-ink-800">
                <SectionHeader eyebrow={summaryDate === new Date().toISOString().slice(0,10) ? "Today's intakes" : `Intakes on ${summaryDate}`} title="Recent Activity" />
              </div>
              {dayIntaked.length === 0 ? (
                <div className="p-8"><EmptyState icon={<Icon.Check className="w-6 h-6" />} title="No intakes on this date" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead><tr><th>Batch</th><th>Farmer</th><th>Weight</th><th>Grade</th><th>Score</th><th>Amount</th><th>Warehouse</th><th>Time</th></tr></thead>
                    <tbody>
                      {dayIntaked.map((b: any) => (
                        <tr key={b.id}>
                          <td className="text-xs font-mono">{b.id.slice(0, 8)}…</td>
                          <td className="text-sm">{b.farmer_name || '—'}</td>
                          <td className="nums">{b.actual_weight} kg</td>
                          <td><StatusPill tone={b.quality_grade === 'A' ? 'success' : b.quality_grade === 'B' ? 'warning' : 'danger'} dot>{b.quality_grade}</StatusPill></td>
                          <td className="nums">{b.quality_score || '—'}</td>
                          <td className="nums">{Number(b.calculated_amount || 0).toLocaleString()} XAF</td>
                          <td className="text-2xs">{b.warehouse_section || '—'}</td>
                          <td className="text-2xs">{b.intaked_at ? new Date(b.intaked_at).toLocaleTimeString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* v1.6.0: Batch Decision Routing (Phase 9) */}
            <Card padding="none" className="overflow-hidden">
              <div className="p-5 border-b border-ink-200 dark:border-ink-800">
                <SectionHeader eyebrow="Phase 9 — Batch Decision" title="Route Batches" subtitle="Send premium cocoa to export, standard to factory, reject defects" />
              </div>
              {intakedBatches.filter(b => !b.batch_decision).length === 0 ? (
                <div className="p-8"><EmptyState icon={<Icon.Check className="w-5 h-5" />} title="All batches decided" description="Every intaked batch has been routed." /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead><tr><th>Batch</th><th>Farmer</th><th>Grade</th><th>Score</th><th>Weight</th><th>Decision</th></tr></thead>
                    <tbody>
                      {intakedBatches.filter(b => !b.batch_decision).map((b: any) => (
                        <tr key={b.id}>
                          <td className="text-xs font-mono">{b.id.slice(0, 8)}…</td>
                          <td className="text-sm">{b.farmer_name || '—'}</td>
                          <td><StatusPill tone={b.quality_grade === 'A' ? 'success' : b.quality_grade === 'B' ? 'warning' : 'danger'} dot>{b.quality_grade}</StatusPill></td>
                          <td className="nums">{b.quality_score || '—'}</td>
                          <td className="nums">{b.actual_weight} kg</td>
                          <td>
                            <select
                              defaultValue=""
                              onChange={(e) => {
                                if (e.target.value && confirm(`Route this batch to ${e.target.value}?`)) {
                                  handleBatchDecision(b.id, e.target.value);
                                }
                              }}
                              className="input text-xs"
                            >
                              <option value="">— Select —</option>
                              <option value="EXPORT">Export</option>
                              <option value="FACTORY">Factory</option>
                              <option value="RECONDITION">Recondition</option>
                              <option value="DOWNGRADE">Downgrade</option>
                              <option value="REJECT">Reject</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* v1.6.0: Variance Alerts (Phase 7) — v1.7.1 (M-04): uses Sheet instead of prompt() */}
            {intakedBatches.filter(b => b.variance_pct != null && Math.abs(b.variance_pct) > 2 && !b.variance_investigated).length > 0 && (
              <Card padding="none" className="overflow-hidden border-2 border-danger-500/30">
                <div className="p-5 border-b border-danger-500/20 bg-danger-50 dark:bg-danger-500/10">
                  <SectionHeader eyebrow="⚠️ Phase 7 — Variance Alert" title="Weight Variance Investigations" subtitle="Batches with >2% weight difference between dispatch and receiving" />
                </div>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead><tr><th>Batch</th><th>Expected</th><th>Received</th><th>Variance</th><th>%</th><th>Action</th></tr></thead>
                    <tbody>
                      {intakedBatches.filter(b => b.variance_pct != null && Math.abs(b.variance_pct) > 2 && !b.variance_investigated).map((b: any) => (
                        <tr key={b.id} className="bg-danger-50/50 dark:bg-danger-500/5">
                          <td className="text-xs font-mono">{b.id.slice(0, 8)}…</td>
                          <td className="nums">{Number(b.expected_weight_kg || 0).toFixed(1)} kg</td>
                          <td className="nums">{Number(b.received_weight_kg || b.actual_weight || 0).toFixed(1)} kg</td>
                          <td className={`nums font-medium ${Number(b.variance_kg) < 0 ? 'text-danger-600 dark:text-danger-500' : 'text-warning-600 dark:text-warning-500'}`}>
                            {Number(b.variance_kg || 0).toFixed(1)} kg
                          </td>
                          <td className={`nums font-medium ${Math.abs(Number(b.variance_pct)) > 5 ? 'text-danger-600 dark:text-danger-500' : 'text-warning-600 dark:text-warning-500'}`}>
                            {Number(b.variance_pct || 0).toFixed(2)}%
                          </td>
                          <td>
                            <button
                              onClick={() => openVarianceSheet(b)}
                              className="btn-secondary text-xs"
                            >
                              Investigate
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* end v1.7.1 (L-02) tabLoading conditional wrapper */}
        </>
        )}
      </main>

      {/* ─── Intake Sheet (modal) ─── */}
      <Sheet open={!!intakeSheet} onClose={() => { setIntakeSheet(null); setAiGrade(null); }} title="Batch Intake & Quality Grading"
        footer={<IntakeSheetFooter onCancel={() => { setIntakeSheet(null); setAiGrade(null); }} />}>
        {intakeSheet && (
          <IntakeForm
            batch={intakeSheet}
            aiGrade={aiGrade}
            aiGrading={aiGrading}
            onPreviewGrade={previewAiGrade}
            onSubmit={submitIntake}
          />
        )}
      </Sheet>

      {/* v1.7.1 (M-04): Variance Investigation Sheet — replaces native prompt() */}
      <Sheet
        open={!!varianceSheet}
        onClose={() => { setVarianceSheet(null); setVarianceNotes(''); }}
        title="Investigate Weight Variance"
        footer={
          <div className="flex gap-2">
            <button
              onClick={() => { setVarianceSheet(null); setVarianceNotes(''); }}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              onClick={() => varianceSheet && handleInvestigateVariance(varianceSheet.batchId, varianceNotes)}
              disabled={!varianceNotes.trim()}
              className="btn-primary flex-1"
            >
              <Icon.Check className="w-4 h-4" /> Record Investigation
            </button>
          </div>
        }
      >
        {varianceSheet && (
          <div className="space-y-4">
            <div className="bg-danger-50 dark:bg-danger-500/10 border border-danger-200 dark:border-danger-500/30 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2 text-danger-700 dark:text-danger-400">
                <Icon.Alert className="w-4 h-4" />
                <span className="text-sm font-semibold">Weight variance detected</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="text-ink-500">Expected</div>
                  <div className="nums font-medium">{Number(varianceSheet.batch.expected_weight_kg || 0).toFixed(1)} kg</div>
                </div>
                <div>
                  <div className="text-ink-500">Received</div>
                  <div className="nums font-medium">{Number(varianceSheet.batch.received_weight_kg || varianceSheet.batch.actual_weight || 0).toFixed(1)} kg</div>
                </div>
                <div>
                  <div className="text-ink-500">Variance</div>
                  <div className={`nums font-medium ${Number(varianceSheet.batch.variance_pct) > 5 ? 'text-danger-600' : 'text-warning-600'}`}>
                    {Number(varianceSheet.batch.variance_pct || 0).toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="variance-notes" className="label">Investigation notes *</label>
              <p className="text-2xs text-ink-500 mb-2">What caused the variance? (e.g. moisture loss, spillage, scale error, theft)</p>
              <textarea
                id="variance-notes"
                value={varianceNotes}
                onChange={(e) => setVarianceNotes(e.target.value)}
                className="input"
                rows={4}
                placeholder="e.g. Truck tarps were loose during transport — approximately 3 kg of cocoa lost to wind on the highway."
                autoFocus
              />
            </div>
          </div>
        )}
      </Sheet>

      {/* v1.9.2: Sensory QC Tab — clerk records organoleptic tests at intake */}
      {activeTab === 'sensory' && <ClerkSensoryTab />}

      {/* v1.9.2: Lab Tests Tab — clerk records MRL/cadmium test results */}
      {activeTab === 'lab' && <ClerkLabTab />}
          <CopilotWidget userRole="factory_clerk" />
</div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.9.2 — Clerk Sensory QC Tab (organoleptic testing at intake)
 * ═══════════════════════════════════════════════════════════════ */
const ClerkSensoryTab: React.FC = () => {
  const [tests, setTests] = useState<any[]>([]);
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    batch_id: '', aroma_score: '', flavor_score: '', body_score: '',
    smoke_odor_detected: false, smoke_odor_intensity: 'NONE',
    smoke_taste_detected: false, smoke_taste_intensity: 'NONE', notes: ''
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const [tRes, bRes] = await Promise.all([
        apiClient.get('/organoleptic').catch(() => ({ data: { tests: [] } })),
        apiClient.get('/batches?status=INTAKED&limit=50').catch(() => ({ data: [] })),
      ]);
      setTests(tRes.data?.tests || []);
      const bArr = Array.isArray(bRes.data) ? bRes.data : (bRes.data?.batches || []);
      setBatches(bArr);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    setError(null);
    try {
      const payload: any = { ...form };
      ['aroma_score', 'flavor_score', 'body_score'].forEach(f => {
        payload[f] = payload[f] ? parseFloat(payload[f]) : undefined;
      });
      if (payload.smoke_odor_intensity === 'NONE') payload.smoke_odor_intensity = null;
      if (payload.smoke_taste_intensity === 'NONE') payload.smoke_taste_intensity = null;
      const res = await apiClient.post('/organoleptic', payload);
      setSuccess(res.data.message);
      setShowCreate(false);
      setForm({ batch_id: '', aroma_score: '', flavor_score: '', body_score: '', smoke_odor_detected: false, smoke_odor_intensity: 'NONE', smoke_taste_detected: false, smoke_taste_intensity: 'NONE', notes: '' });
      refresh();
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to create test'); }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center">
          <SectionHeader eyebrow="Sensory Analysis" title="Organoleptic Tests" subtitle="Record aroma, flavor, smoke contamination at intake" icon={<Icon.Sparkles className="w-4 h-4" />} />
          <button onClick={() => setShowCreate(true)} className="btn-primary text-xs"><Icon.Package className="w-4 h-4" /> Add Test</button>
        </div>
        {tests.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Sparkles className="w-5 h-5" />} title="No sensory tests" description="Record a sensory analysis for an INTAKED batch." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Batch</th><th>Farmer</th><th>Score</th><th>Smoke Odor</th><th>Smoke Taste</th><th>Disposition</th></tr></thead>
              <tbody>
                {tests.map(t => (
                  <tr key={t.id} className={t.disposition === 'REJECTED' ? 'bg-danger-50/50 dark:bg-danger-500/5' : ''}>
                    <td className="text-xs font-mono">{t.batch_id_short?.slice(0, 8)}...</td>
                    <td className="text-sm">{t.farmer_name || '---'}</td>
                    <td className={`nums font-medium ${Number(t.overall_score) >= 8 ? 'text-success-600' : Number(t.overall_score) >= 6 ? 'text-warning-600' : 'text-danger-600'}`}>{t.overall_score ? Number(t.overall_score).toFixed(1) : '---'}</td>
                    <td>{t.smoke_odor_detected ? <StatusPill tone={t.smoke_odor_intensity === 'SEVERE' || t.smoke_odor_intensity === 'STRONG' ? 'danger' : 'warning'} dot>{t.smoke_odor_intensity}</StatusPill> : <span className="text-success-600 text-xs">None</span>}</td>
                    <td>{t.smoke_taste_detected ? <StatusPill tone={t.smoke_taste_intensity === 'SEVERE' || t.smoke_taste_intensity === 'STRONG' ? 'danger' : 'warning'} dot>{t.smoke_taste_intensity}</StatusPill> : <span className="text-success-600 text-xs">None</span>}</td>
                    <td><StatusPill tone={t.disposition === 'APPROVED_PREMIUM' ? 'success' : t.disposition === 'REJECTED' ? 'danger' : 'warning'} dot>{t.disposition.replace(/_/g, ' ')}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && (
        <Sheet open={showCreate} onClose={() => setShowCreate(false)} title="Add Sensory Test"
          footer={<div className="flex gap-2"><button onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button><button onClick={handleCreate} disabled={!form.batch_id} className="btn-primary flex-1">Create Test</button></div>}>
          <div className="space-y-3">
            <div><label className="label">Batch *</label><select className="input" value={form.batch_id} onChange={e => setForm({ ...form, batch_id: e.target.value })}><option value="">Select batch</option>{batches.map((b: any) => <option key={b.id} value={b.id}>{b.id.slice(0, 8)}... - {b.farmer_name}</option>)}</select></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="label">Aroma (1-10)</label><input type="number" step="0.1" min="1" max="10" className="input" value={form.aroma_score} onChange={e => setForm({ ...form, aroma_score: e.target.value })} /></div>
              <div><label className="label">Flavor (1-10)</label><input type="number" step="0.1" min="1" max="10" className="input" value={form.flavor_score} onChange={e => setForm({ ...form, flavor_score: e.target.value })} /></div>
              <div><label className="label">Body (1-10)</label><input type="number" step="0.1" min="1" max="10" className="input" value={form.body_score} onChange={e => setForm({ ...form, body_score: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.smoke_odor_detected} onChange={e => setForm({ ...form, smoke_odor_detected: e.target.checked })} className="w-4 h-4" /><span>Smoke odor</span></label>{form.smoke_odor_detected && <select className="input mt-1" value={form.smoke_odor_intensity} onChange={e => setForm({ ...form, smoke_odor_intensity: e.target.value })}><option value="NONE">None</option><option value="FAINT">Faint</option><option value="MODERATE">Moderate</option><option value="STRONG">Strong</option><option value="SEVERE">Severe</option></select>}</div>
              <div><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.smoke_taste_detected} onChange={e => setForm({ ...form, smoke_taste_detected: e.target.checked })} className="w-4 h-4" /><span>Smoke taste</span></label>{form.smoke_taste_detected && <select className="input mt-1" value={form.smoke_taste_intensity} onChange={e => setForm({ ...form, smoke_taste_intensity: e.target.value })}><option value="NONE">None</option><option value="FAINT">Faint</option><option value="MODERATE">Moderate</option><option value="STRONG">Strong</option><option value="SEVERE">Severe</option></select>}</div>
            </div>
            <Alert tone="info">Strong/severe smoke = auto-reject + quarantine. Moderate = hold + 15% discount. Faint = secondary grade + 5% discount.</Alert>
          </div>
        </Sheet>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.9.2 — Clerk Lab Tests Tab (MRL/cadmium recording at intake)
 * ═══════════════════════════════════════════════════════════════ */
const ClerkLabTab: React.FC = () => {
  const [tests, setTests] = useState<any[]>([]);
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    batch_id: '', test_reference: '', lab_name: '', test_date: new Date().toISOString().slice(0, 10),
    cadmium_mg_kg: '', overall_result: 'CONDITIONAL', notes: ''
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const [tRes, bRes] = await Promise.all([
        apiClient.get('/lab-tests').catch(() => ({ data: { lab_tests: [] } })),
        apiClient.get('/batches?status=INTAKED&limit=50').catch(() => ({ data: [] })),
      ]);
      setTests(tRes.data?.lab_tests || []);
      const bArr = Array.isArray(bRes.data) ? bRes.data : (bRes.data?.batches || []);
      setBatches(bArr);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    setError(null);
    try {
      await apiClient.post('/lab-tests', {
        ...form,
        cadmium_mg_kg: form.cadmium_mg_kg ? parseFloat(form.cadmium_mg_kg) : undefined,
      });
      setSuccess('Lab test recorded!');
      setShowCreate(false);
      setForm({ batch_id: '', test_reference: '', lab_name: '', test_date: new Date().toISOString().slice(0, 10), cadmium_mg_kg: '', overall_result: 'CONDITIONAL', notes: '' });
      refresh();
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to create lab test'); }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center">
          <SectionHeader eyebrow="EU Compliance" title="Lab Tests (MRL/Cadmium)" subtitle="Record heavy metal + pesticide test results" icon={<Icon.Package className="w-4 h-4" />} />
          <button onClick={() => setShowCreate(true)} className="btn-primary text-xs"><Icon.Package className="w-4 h-4" /> Add Test</button>
        </div>
        {tests.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Package className="w-5 h-5" />} title="No lab tests" description="Record a lab test to enable EU export routing." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Test Ref</th><th>Batch</th><th>Farmer</th><th>Lab</th><th>Cadmium</th><th>Result</th></tr></thead>
              <tbody>
                {tests.map(t => (
                  <tr key={t.id}>
                    <td className="text-xs font-mono">{t.test_reference}</td>
                    <td className="text-xs font-mono">{t.batch_id_short?.slice(0, 8)}...</td>
                    <td className="text-sm">{t.farmer_name || '---'}</td>
                    <td className="text-2xs">{t.lab_name}</td>
                    <td className={`nums ${t.cadmium_pass === false ? 'text-danger-600 font-bold' : t.cadmium_pass ? 'text-success-600' : ''}`}>{t.cadmium_mg_kg ? `${Number(t.cadmium_mg_kg).toFixed(3)} mg/kg` : '---'}</td>
                    <td><StatusPill tone={t.overall_result === 'PASS' ? 'success' : t.overall_result === 'FAIL' ? 'danger' : 'warning'} dot>{t.overall_result}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && (
        <Sheet open={showCreate} onClose={() => setShowCreate(false)} title="Add Lab Test"
          footer={<div className="flex gap-2"><button onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button><button onClick={handleCreate} disabled={!form.batch_id || !form.test_reference || !form.lab_name} className="btn-primary flex-1">Create Test</button></div>}>
          <div className="space-y-3">
            <div><label className="label">Batch *</label><select className="input" value={form.batch_id} onChange={e => setForm({ ...form, batch_id: e.target.value })}><option value="">Select batch</option>{batches.map((b: any) => <option key={b.id} value={b.id}>{b.id.slice(0, 8)}... - {b.farmer_name}</option>)}</select></div>
            <div><label className="label">Test Reference *</label><input className="input" value={form.test_reference} onChange={e => setForm({ ...form, test_reference: e.target.value })} placeholder="e.g. LAB-2026-001" /></div>
            <div><label className="label">Lab Name *</label><input className="input" value={form.lab_name} onChange={e => setForm({ ...form, lab_name: e.target.value })} placeholder="e.g. SGS Cameroon" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Test Date *</label><input type="date" className="input" value={form.test_date} onChange={e => setForm({ ...form, test_date: e.target.value })} /></div>
              <div><label className="label">Cadmium (mg/kg)</label><input type="number" step="0.001" className="input" value={form.cadmium_mg_kg} onChange={e => setForm({ ...form, cadmium_mg_kg: e.target.value })} placeholder="0.8 max" /></div>
            </div>
            <div><label className="label">Result</label><select className="input" value={form.overall_result} onChange={e => setForm({ ...form, overall_result: e.target.value })}><option value="PASS">PASS</option><option value="FAIL">FAIL</option><option value="CONDITIONAL">CONDITIONAL</option></select></div>
            <Alert tone="info">EU cadmium limit for raw cocoa: 0.8 mg/kg. Batches exceeding this cannot be routed to EXPORT.</Alert>
          </div>
        </Sheet>
      )}
    </div>
  );
};

// ─── Intake Form (inside the Sheet) ─────────────────────────
const IntakeForm: React.FC<{
  batch: any;
  aiGrade: any | null;
  aiGrading: boolean;
  onPreviewGrade: (formData: any) => void;
  onSubmit: (formData: any) => void;
}> = ({ batch, aiGrade, aiGrading, onPreviewGrade, onSubmit }) => {
  const [form, setForm] = useState({
    actual_weight: '',
    moisture_content: '',
    fermentation_level: 'Full',
    spoilage_kg: '0',
    bean_count_per_100g: '',
    defect_insect_kg: '0',
    defect_mold_kg: '0',
    defect_flat_kg: '0',
    temperature_c: '',
    humidity_pct: '',
    batch_photo_url: '',
    warehouse_section: '',
    notes: '',
  });
  // v1.7.1 (M-05): local preview of uploaded photo (base64 data URL)
  const [photoPreview, setPhotoPreview] = useState<string>('');

  const update = (field: string, value: string) => setForm({ ...form, [field]: value });

  // v1.7.1 (C-02): real-time variance detection — compare actual weight vs expected
  const expectedWeight = Number(batch.expected_weight_kg || batch.est_weight || 0);
  const actualWeight = Number(form.actual_weight || 0);
  const varianceKg = expectedWeight > 0 ? actualWeight - expectedWeight : 0;
  const variancePct = expectedWeight > 0 ? (varianceKg / expectedWeight) * 100 : 0;
  const showVarianceWarning = expectedWeight > 0 && actualWeight > 0 && Math.abs(variancePct) > 2;
  const isHighVariance = expectedWeight > 0 && actualWeight > 0 && Math.abs(variancePct) > 5;

  // v1.7.1 (M-05): handle file upload — converts to base64 data URL for AI grading + preview
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Photo too large — please choose an image under 5 MB.');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setPhotoPreview(dataUrl);
      update('batch_photo_url', dataUrl);  // AI grading endpoint accepts base64
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4">
      {/* Batch info */}
      <div className="bg-ink-100 dark:bg-ink-800 rounded-lg p-3 space-y-1">
        <div className="flex justify-between"><span className="text-xs text-ink-500">Batch</span><span className="text-xs font-mono">{batch.id?.slice(0, 8)}…</span></div>
        <div className="flex justify-between"><span className="text-xs text-ink-500">Farmer</span><span className="text-xs">{batch.farmer_name || '—'}</span></div>
        <div className="flex justify-between"><span className="text-xs text-ink-500">Est. Weight</span><span className="text-xs nums">{batch.est_weight} kg</span></div>
        {/* v1.7.1 (M-02): show dispatch info if available */}
        {(batch.truck_reg || batch.driver_name) && (
          <>
            <div className="flex justify-between"><span className="text-xs text-ink-500">Truck</span><span className="text-xs font-mono">{batch.truck_reg || '—'}</span></div>
            <div className="flex justify-between"><span className="text-xs text-ink-500">Driver</span><span className="text-xs">{batch.driver_name || '—'}</span></div>
          </>
        )}
      </div>

      {/* Weight */}
      <div>
        <label htmlFor="actual_weight" className="label">Actual weight (kg) *</label>
        <input id="actual_weight" type="number" step="0.01" value={form.actual_weight}
          onChange={(e) => update('actual_weight', e.target.value)} className="input" required />
        {/* v1.7.1 (C-02): real-time variance warning */}
        {showVarianceWarning && (
          <div className={`mt-2 p-2 rounded-lg border text-xs flex items-start gap-2 ${
            isHighVariance
              ? 'bg-danger-50 dark:bg-danger-500/10 border-danger-200 dark:border-danger-500/30 text-danger-700 dark:text-danger-400'
              : 'bg-warning-50 dark:bg-warning-500/10 border-warning-200 dark:border-warning-500/30 text-warning-700 dark:text-warning-400'
          }`}>
            <Icon.Alert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">
                {isHighVariance ? 'High' : 'Minor'} weight variance: {varianceKg > 0 ? '+' : ''}{varianceKg.toFixed(1)} kg ({variancePct.toFixed(1)}%)
              </div>
              <div className="text-2xs mt-0.5 opacity-90">
                Expected {expectedWeight.toFixed(1)} kg from dispatch. {varianceKg < 0
                  ? 'Cocoa may have been lost in transit — investigate before confirming.'
                  : 'Weight gain is unusual — verify scale calibration.'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quality metrics */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="moisture" className="label">Moisture (%)</label>
          <input id="moisture" type="number" step="0.1" value={form.moisture_content}
            onChange={(e) => update('moisture_content', e.target.value)} className="input" placeholder="6.5-7.5 ideal" />
        </div>
        <div>
          <label htmlFor="fermentation" className="label">Fermentation</label>
          <select id="fermentation" value={form.fermentation_level}
            onChange={(e) => update('fermentation_level', e.target.value)} className="input">
            <option value="Under">Under-fermented</option>
            <option value="Full">Fully-fermented</option>
            <option value="Over">Over-fermented</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="bean_count" className="label">Bean count / 100g</label>
          <input id="bean_count" type="number" value={form.bean_count_per_100g}
            onChange={(e) => update('bean_count_per_100g', e.target.value)} className="input" placeholder="90-110 = premium" />
        </div>
        <div>
          <label htmlFor="spoilage" className="label">Spoilage (kg)</label>
          <input id="spoilage" type="number" step="0.01" value={form.spoilage_kg}
            onChange={(e) => update('spoilage_kg', e.target.value)} className="input" />
        </div>
      </div>

      {/* Defects */}
      <div>
        <p className="text-xs font-medium uppercase tracking-micro text-ink-500 mb-2">Defects (kg)</p>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label htmlFor="defect_insect" className="text-2xs text-ink-500">Insect</label>
            <input id="defect_insect" type="number" step="0.001" value={form.defect_insect_kg}
              onChange={(e) => update('defect_insect_kg', e.target.value)} className="input text-sm" />
          </div>
          <div>
            <label htmlFor="defect_mold" className="text-2xs text-ink-500">Mold</label>
            <input id="defect_mold" type="number" step="0.001" value={form.defect_mold_kg}
              onChange={(e) => update('defect_mold_kg', e.target.value)} className="input text-sm" />
          </div>
          <div>
            <label htmlFor="defect_flat" className="text-2xs text-ink-500">Flat</label>
            <input id="defect_flat" type="number" step="0.001" value={form.defect_flat_kg}
              onChange={(e) => update('defect_flat_kg', e.target.value)} className="input text-sm" />
          </div>
        </div>
      </div>

      {/* Environmental */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="temp" className="label">Temperature (°C)</label>
          <input id="temp" type="number" step="0.1" value={form.temperature_c}
            onChange={(e) => update('temperature_c', e.target.value)} className="input" />
        </div>
        <div>
          <label htmlFor="humidity" className="label">Humidity (%)</label>
          <input id="humidity" type="number" step="0.1" value={form.humidity_pct}
            onChange={(e) => update('humidity_pct', e.target.value)} className="input" />
        </div>
      </div>

      {/* Photo URL + warehouse — v1.7.1 (M-05): file upload with preview (URL still accepted as fallback) */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="photo-upload" className="label">Bean photo</label>
          {/* v1.7.1 (M-05): preferred path — file upload with live preview */}
          <input
            id="photo-upload"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoUpload}
            className="input text-2xs p-1.5"
          />
          {/* v1.7.1 (M-05): live preview of uploaded photo */}
          {photoPreview && (
            <div className="mt-2 relative">
              <img
                src={photoPreview}
                alt="Bean preview"
                className="w-full h-32 object-cover rounded-lg border border-ink-200 dark:border-ink-800"
              />
              <button
                type="button"
                onClick={() => { setPhotoPreview(''); update('batch_photo_url', ''); }}
                className="absolute top-1 right-1 bg-ink-900/80 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-ink-900"
                aria-label="Remove photo"
              >
                ×
              </button>
            </div>
          )}
          {/* v1.7.1 (M-05): fallback — paste URL (kept for backward compat) */}
          <input
            id="photo"
            type="url"
            value={form.batch_photo_url && !form.batch_photo_url.startsWith('data:') ? form.batch_photo_url : ''}
            onChange={(e) => { update('batch_photo_url', e.target.value); setPhotoPreview(''); }}
            className="input mt-2 text-2xs"
            placeholder="…or paste image URL"
          />
        </div>
        <div>
          <label htmlFor="warehouse" className="label">Warehouse section</label>
          <input id="warehouse" type="text" value={form.warehouse_section}
            onChange={(e) => update('warehouse_section', e.target.value)} className="input" placeholder="A-1" />
        </div>
      </div>

      <div>
        <label htmlFor="notes" className="label">Notes</label>
        <textarea id="notes" value={form.notes} onChange={(e) => update('notes', e.target.value)} className="input" rows={2} />
      </div>

      {/* AI Grade Preview */}
      <div className="border-t border-ink-200 dark:border-ink-800 pt-4">
        <button
          onClick={() => onPreviewGrade(form)}
          disabled={aiGrading || !form.actual_weight}
          className="btn-secondary w-full"
        >
          {aiGrading ? <><Spinner size="sm" /> Analyzing with AI…</> : <><Icon.Sparkles className="w-4 h-4" /> Preview AI Grade</>}
        </button>

        {aiGrade && (
          <div className="mt-3 p-4 bg-ink-100 dark:bg-ink-800 rounded-lg space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">AI Grade Preview <span className="clerk-ai-advisory">Advisory</span></span>
              <StatusPill tone={aiGrade.grade === 'A' ? 'success' : aiGrade.grade === 'B' ? 'warning' : 'danger'} dot>
                Grade {aiGrade.grade} · Score {aiGrade.score}/100
              </StatusPill>
            </div>
            <div className="text-2xs text-ink-500">Confidence: {(aiGrade.confidence * 100).toFixed(0)}%</div>
            <p className="text-xs text-ink-600 dark:text-ink-300">{aiGrade.reasoning}</p>
            {aiGrade.visual_observations && (
              <div className="text-2xs text-ink-500 mt-2">
                <div>Color: {aiGrade.visual_observations.color || '—'}</div>
                <div>Size uniformity: {aiGrade.visual_observations.size_uniformity || '—'}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Submit — v1.7.1 (M-01): confirmation dialog before submitting intake */}
      <button
        onClick={() => {
          // v1.7.1 (M-01): confirm before irreversible intake action.
          // Include variance warning in the confirmation message when applicable.
          const weightInfo = `Actual weight: ${form.actual_weight} kg`;
          const varianceInfo = showVarianceWarning
            ? `\n\n⚠️ Weight variance of ${varianceKg > 0 ? '+' : ''}${varianceKg.toFixed(1)} kg (${variancePct.toFixed(1)}%) detected. Please confirm this is correct.`
            : '';
          const gradeInfo = aiGrade ? `\n\nAI Grade preview: ${aiGrade.grade} (score ${aiGrade.score})` : '';
          if (confirm(`Confirm intake for batch ${batch.id?.slice(0, 8)}…?\n\n${weightInfo}${varianceInfo}${gradeInfo}`)) {
            onSubmit(form);
          }
        }}
        disabled={!form.actual_weight}
        className="btn-primary w-full"
      >
        <Icon.Check className="w-4 h-4" /> Confirm Intake
      </button>
    </div>
  );
};

// ─── Sheet footer (hidden — we put submit inside the form) ──
const IntakeSheetFooter: React.FC<{ onCancel: () => void }> = ({ onCancel }) => (
  <button onClick={onCancel} className="btn-secondary w-full">Cancel</button>
);

export default FactoryClerkDashboard;
